import { BOOKING_WHERE_LABELS } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PaymentLinkShell } from '../components/paymentLinks/PaymentLinkShell';
import { fetchBookingPerson } from '../services/bookingService';
import { BookingNotice } from './BookingPage';

/** PD-17646 — `/book/:person`: the meetings a person offers. */
export function BookingPersonPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { person = '' } = useParams();

	const { data, isLoading } = useQuery({
		queryKey: ['booking-person', person],
		queryFn: () => fetchBookingPerson(person),
		retry: false,
	});

	useEffect(() => {
		if (data?.redirectTo != null) {
			void navigate(data.redirectTo, { replace: true });
		}
	}, [data?.redirectTo, navigate]);

	if (isLoading) {
		return <PaymentLinkShell secureLabel={null} showSignIn={false}><div className="min-h-64" /></PaymentLinkShell>;
	}

	if (data == null) {
		return <BookingNotice title="This booking page is not available" body="Check that you opened the whole link, or contact the person who sent it." />;
	}

	return (
		<PaymentLinkShell secureLabel={null} showSignIn={false}>
			<div className="space-y-4">
				<div>
					<h1 className="text-xl font-bold text-default">{t(data.hostName)}</h1>
					<p className="text-sm text-brand-muted">{t(data.companyName)}</p>
				</div>
				{data.types.length === 0 ? <p className="text-brand-muted">{t('No times are offered right now.')}</p> : null}
				{data.types.map((type) => (
					<Link
						key={type.name}
						to={type.path ?? `/book/${person}/${type.name}`}
						className="block bg-brand-surface border border-brand-border rounded-xl p-5 hover:border-portal-accent"
					>
						<h2 className="font-semibold text-default">{t(type.title)}</h2>
						<p className="text-sm text-brand-muted">
							{t('{{count}} minutes', { count: type.lengthMinutes })} · {t(type.whereDetail ?? BOOKING_WHERE_LABELS[type.whereKind])}
						</p>
						{type.description != null && type.description !== '' ? (
							<p className="mt-2 text-sm text-default">{t(type.description)}</p>
						) : null}
					</Link>
				))}
			</div>
		</PaymentLinkShell>
	);
}
